// src/components/Dashboard/Dashboard.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth, db } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  getDoc
} from "firebase/firestore";
import { useDashboardContext } from "../../contexts/DashboardContext"; // Import the context
import { useUserContext } from "../../contexts/UserContext"; // Importar o contexto de usuário
import MainLayout from "../Layout/MainLayout";
import "./Dashboard.css";

// Importar serviços refatorados - REUTILIZAÇÃO DE CÓDIGO
import {
  loadAvailablePeriods,
  loadCategoryMappings,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

import { checkTransactionCategorization } from "../../services/categorizationService";

// Função para carregar tipos de custo do Firestore
const loadCostTypes = async (userId) => {
  try {
    const costTypeDoc = await getDoc(doc(db, "userCostTypes", userId));

    if (costTypeDoc.exists()) {
      return costTypeDoc.data().types || {};
    }

    return {}; // Retornar objeto vazio se não existir configuração
  } catch (error) {
    console.error("Erro ao carregar tipos de custo:", error);
    return {};
  }
};

const Dashboard = () => {
  // Get cached data from context - PROGRESSIVE LOADING APPROACH
  const {
    cachedFinancialData,
    isPeriodDataCached,
    cacheFinancialData,
    cacheInitialPeriods,
    cacheAdditionalPeriods,
    markAllPeriodsLoaded,
    getFilteredPeriods,
    getLoadingStatus
  } = useDashboardContext();

  // Obter o tipo de conta ativo do contexto do usuário E a função getAccountSubpath
  const { activeAccountType, getAccountSubpath, activeBusinessProfileId } = useUserContext();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [error, setError] = useState("");
  const [financialData, setFinancialData] = useState({
    receitaBruta: 0,
    custosDiretos: 0,
    custosServicosPrestados: 0,
    despesasOperacionais: 0,
    deducoesReceita: 0, // Adicionado para consistência, embora o principal seja no loadFinancialData
    despesasTotais: 0,
    lucroBruto: 0,
    resultadoFinal: 0,
    aportesValue: 0,
    resultadoFinalComAportes: 0
  });
  const [currentPeriod, setCurrentPeriod] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [periods, setPeriods] = useState([]);
  const [loadingFinancialData, setLoadingFinancialData] = useState(false);

  // Adicionar estado para o modelo de negócio
  const [businessModel, setBusinessModel] = useState(null);

  // Estados para custos fixos e variáveis
  const [costTypes, setCostTypes] = useState({});
  const [costData, setCostData] = useState({
    fixedCost: 0,
    variableCost: 0,
    uncategorizedCost: 0,
    fixedPercent: 0,
    variablePercent: 0,
    uncategorizedPercent: 0
  });

  // Nova variável de estado para verificar se deve mostrar a opção de aportes
  const [showAportesOption, setShowAportesOption] = useState(false);

  // Load UI preferences from localStorage with fallbacks - ALTERADO NOME DA VARIÁVEL
  const [includeExcluidos, setIncludeExcluidos] = useState(() => {
    // Try to get from localStorage, fallback to false
    const saved = localStorage.getItem('includeExcluidos');
    return saved === 'true' ? true : false;
  });

  const [periodsToShow, setPeriodsToShow] = useState(() => {
    // Try to get from localStorage, fallback to 6
    const saved = localStorage.getItem('periodsToShow');
    return saved ? parseInt(saved) : 6;
  });

  // State to control rendering of "Deduções da Receita" column
  const [shouldShowRevenueDeductions, setShouldShowRevenueDeductions] = useState(() => {
    // If 'hideRevenueDeductionsDashboard' is 'true', then we should NOT show the column.
    // Otherwise (if 'false' or not set/null), we SHOULD show it (default behavior).
    const hideSetting = localStorage.getItem("hideRevenueDeductionsDashboard");
    return hideSetting !== "true";
  });

  // Effect to listen to localStorage changes for this specific setting
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "hideRevenueDeductionsDashboard") {
        setShouldShowRevenueDeductions(event.newValue !== "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Initial check in case the value was set before component mount / listener attachment
    const currentSetting = localStorage.getItem("hideRevenueDeductionsDashboard");
    setShouldShowRevenueDeductions(currentSetting !== "true");

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Period summary approach using progressive loading
  const [periodsSummary, setPeriodsSummary] = useState([]);
  const [loadingPeriodsSummary, setLoadingPeriodsSummary] = useState(false);
  const [loadingBackground, setLoadingBackground] = useState(false);
  const [showBackgroundLoading, setShowBackgroundLoading] = useState(false);

  // Keep track of the background loading task
  const backgroundLoadingRef = useRef(null);
  const backgroundLoadingTimeoutRef = useRef(null);

  // Opções de períodos para o dropdown
  const periodOptions = [
    { value: 3, label: "3 meses" },
    { value: 6, label: "6 meses" },
    { value: 12, label: "12 meses" },
    { value: 24, label: "24 meses" },
    { value: 36, label: "36 meses" },
    { value: 0, label: "Tudo" }
  ];

  // Carregar dados financeiros do período selecionado usando serviços refatorados
  const loadFinancialData = useCallback(async (period) => {
    try {
      // Check if data is already cached
      if (isPeriodDataCached(period)) {
        console.log(`Using cached financial data for period ${period}`);
        const cachedData = cachedFinancialData[period];
        setFinancialData(cachedData);

        // Importante: Também atualizar o costData a partir do cache
        if (cachedData.costData) {
          setCostData(cachedData.costData);
        }

        setLoadingFinancialData(false);
        return;
      }

      setLoadingFinancialData(true);

      if (!auth.currentUser) return;

      // Resetar dados financeiros
      const initialData = {
        receitaBruta: 0,
        custosDiretos: 0,
        custosServicosPrestados: 0, // <<< ADICIONADO
        despesasOperacionais: 0,
        deducoesReceita: 0,
        despesasTotais: 0,
        lucroBruto: 0,
        resultadoFinal: 0,
        outrasReceitas: 0,
        despesasSocios: 0,
        despesasDeSociosValue: 0, // NOVA VARIÁVEL para rastrear a subcategoria específica
        investimentos: 0,
        naoCategorizado: 0,
        totalTransactions: 0,
        totalWithoutOutrasReceitas: 0,
        aportesValue: 0,
        resultadoFinalComAportes: 0,
        // Adicionar campos específicos para modelo pessoal
        despesasFixas: 0,
        despesasVariaveis: 0,
        saldoFinal: 0
      };

      // Buscar mapeamentos usando o serviço refatorado
      const categoryMappings = await loadCategoryMappings();

      // Identificar mapeamentos específicos por ID de transação
      const specificMappings = {};

      // Extrair mapeamentos específicos (para transações únicas)
      Object.entries(categoryMappings).forEach(([key, mapping]) => {
        if (mapping.isSpecificMapping && mapping.transactionId) {
          specificMappings[mapping.transactionId] = mapping;
        }
      });

      // Carregar transações usando o serviço refatorado
      const { transactions: allTransactions } = await loadTransactionsForPeriod(period);

      // Declarar cashEntriesSnapshot no escopo adequado
      let cashEntriesSnapshot = [];

      // Estrutura para armazenar valores por categoria (necessário para modelo pessoal)
      const dreStructure = {};

      // Processar cada transação usando o serviço de categorização
      for (const transaction of allTransactions) {
        // Adicionar ao total geral da soma
        initialData.totalTransactions += transaction.amount;

        let mainCategory = null;
        let isCategorized = false;
        let categoryPath = "";

        // Verificar se a transação tem mapeamento específico
        if (specificMappings[transaction.id]) {
          const specificMapping = specificMappings[transaction.id];
          mainCategory = specificMapping.groupName;
          categoryPath = specificMapping.categoryPath || "";
          isCategorized = true;
        }
        // Usar o serviço de categorização para verificar o status da transação
        else {
          const result = checkTransactionCategorization(transaction, categoryMappings);
          if (result.isCategorized) {
            mainCategory = result.mainCategory;
            categoryPath = result.categoryPath;
            isCategorized = true;
          }
        }

        // Check if this is an "aporte de sócio"
        const normalizedDescription = transaction.description.trim().toLowerCase();
        if ((categoryPath && categoryPath.toLowerCase().includes('aporte')) ||
            normalizedDescription.toLowerCase().includes('aporte')) {
          initialData.aportesValue += transaction.amount;
        }

        // Para modelo pessoal, armazenar valores na dreStructure
        if (businessModel === "pessoal" && isCategorized) {
          if (!dreStructure[mainCategory]) {
            dreStructure[mainCategory] = { total: 0 };
          }
          dreStructure[mainCategory].total += transaction.amount;
        }

        // Categorizar transação com base no mapeamento
        if (isCategorized) {
          // MODIFICADO: Verificação ampliada para reconhecer "💸 Receitas" de diversas formas
          if (mainCategory === "RECEITA" || 
              mainCategory === "💸 Receitas" || 
              (businessModel === "pessoal" && mainCategory === "💸 Receitas") ||
              (activeAccountType === 'personal' && mainCategory === "💸 Receitas") ||
              (activeAccountType === 'personal' && categoryPath && categoryPath.startsWith("💸 Receitas")) ||
              (categoryPath && categoryPath === "💸 Receitas.Receitas")) {
            initialData.receitaBruta += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } else if (mainCategory === "(-) DEDUÇÕES DA RECEITA") {
            initialData.deducoesReceita += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } else if (mainCategory === "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)") {
            initialData.custosDiretos += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } else if (mainCategory === "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)") { // <<< NOVA CONDIÇÃO
            initialData.custosServicosPrestados += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } else if (mainCategory === "(-) DESPESAS OPERACIONAIS") {
            initialData.despesasOperacionais += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } else if (mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
            initialData.outrasReceitas += transaction.amount;
            // IMPORTANTE: Não adicionar à soma para o cálculo do totalWithoutOutrasReceitas
            continue; // Adicionado para garantir que não prossiga para totalWithoutOutrasReceitas
          } else if (mainCategory === "(-) DESPESAS COM SÓCIOS") {
            initialData.despesasSocios += transaction.amount;

            // Verificar se é a subcategoria específica "despesas de sócios"
            const subCategoryName = categoryPath.split('.')[1];
            if (subCategoryName && subCategoryName.toLowerCase() === 'despesas de sócios') {
              initialData.despesasDeSociosValue += transaction.amount;
              // Não adicionar ao totalWithoutOutrasReceitas
            } else {
              // Apenas adicionar ao total se não for a subcategoria específica
              initialData.totalWithoutOutrasReceitas += transaction.amount;
            }
          } else if (mainCategory === "(-) INVESTIMENTOS") {
            initialData.investimentos += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } 
          // ADICIONAR: Verificações para categorias de despesas pessoais
          else if (mainCategory === "🧾 Despesas Fixas" || 
                  (categoryPath && categoryPath.startsWith("🧾 Despesas Fixas"))) {
            initialData.despesasFixas += Math.abs(transaction.amount);
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          } 
          else if (mainCategory === "🍞 Despesas Variáveis" || 
                  (categoryPath && categoryPath.startsWith("🍞 Despesas Variáveis"))) {
            initialData.despesasVariaveis += Math.abs(transaction.amount);
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          }
          else if (mainCategory === "💳 Pagamentos e Transferências" || 
                  (categoryPath && categoryPath.startsWith("💳 Pagamentos e Transferências"))) {
            // Adicionar à categoria naoCategorizado para fins de cálculo em contas pessoais
            initialData.naoCategorizado += transaction.amount;
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          }
        } else {
          // Transação não categorizada - incluir no valor não categorizado
          initialData.naoCategorizado += transaction.amount;
          // Também adicionar ao total sem OUTRAS RECEITAS
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        }
      }

      // 2. Buscar e processar entradas manuais de dinheiro para o período
      try {
        // CORRIGIDO: Usar getAccountSubpath() em vez de lógica hardcoded
        const accountSubpath = getAccountSubpath();
        
        // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
        let userId = auth.currentUser.uid;
        
        // Verificar no documento do usuário se é um colaborador
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists() && userDoc.data().isCollaborator === true) {
          // Verificar informações do workspace
          const invitedBy = userDoc.data().invitedBy;
          const originalInvitation = userDoc.data().originalInvitation || {};
          const profileId = originalInvitation.profileId;
          
          // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
          if (activeBusinessProfileId === profileId && invitedBy) {
            userId = invitedBy; // Usar o ID do proprietário
            console.log(`Dashboard: Colaborador detectado. Carregando entradas do proprietário: ${userId}`);
          }
        }
        
        const cashEntriesQuery = query(
          collection(db, "cashEntries", userId, accountSubpath),
          where("period", "==", period)
        );
        
        const cashEntriesSnapshot = await getDocs(cashEntriesQuery);
        console.log(`Dashboard: Carregadas ${cashEntriesSnapshot.size} entradas manuais de dinheiro`);
        
        // Processar cada entrada manual e adicionar à estrutura DRE
        cashEntriesSnapshot.forEach(doc => {
          const entry = doc.data();
          
          // Verificar se a entrada tem categoria e valor
          if (entry.categoryPath && typeof entry.amount === 'number') {
            const pathParts = entry.categoryPath.split('.');
            
            if (pathParts.length >= 1) {
              const mainCategory = pathParts[0];
              
              // Adicionar ao total geral da soma
              initialData.totalTransactions += entry.amount;
              
              // Check if this is an "aporte de sócio"
              if ((entry.categoryPath && entry.categoryPath.toLowerCase().includes('aporte')) ||
                  (entry.description && entry.description.toLowerCase().includes('aporte'))) {
                initialData.aportesValue += entry.amount;
              }
              
              // Para modelo pessoal, armazenar valores na dreStructure
              if (businessModel === "pessoal") {
                if (!dreStructure[mainCategory]) {
                  dreStructure[mainCategory] = { total: 0 };
                }
                dreStructure[mainCategory].total += entry.amount;
              }
              
              // Categorizar com base no grupo principal
              // MODIFICADO: Verificação ampliada para reconhecer "💸 Receitas" de diversas formas
              if (mainCategory === "RECEITA" || 
                  mainCategory === "💸 Receitas" || 
                  (businessModel === "pessoal" && mainCategory === "💸 Receitas") ||
                  (activeAccountType === 'personal' && mainCategory === "💸 Receitas") ||
                  (activeAccountType === 'personal' && entry.categoryPath && entry.categoryPath.startsWith("💸 Receitas")) ||
                  (entry.categoryPath && entry.categoryPath === "💸 Receitas.Receitas")) {
                initialData.receitaBruta += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } else if (mainCategory === "(-) DEDUÇÕES DA RECEITA") {
                initialData.deducoesReceita += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } else if (mainCategory === "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)") {
                initialData.custosDiretos += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } else if (mainCategory === "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)") { // <<< NOVA CONDIÇÃO
                initialData.custosServicosPrestados += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } else if (mainCategory === "(-) DESPESAS OPERACIONAIS") {
                initialData.despesasOperacionais += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } else if (mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
                initialData.outrasReceitas += entry.amount;
                // IMPORTANTE: Não adicionar à soma para o cálculo do totalWithoutOutrasReceitas
              } else if (mainCategory === "(-) DESPESAS COM SÓCIOS") {
                initialData.despesasSocios += entry.amount;
                
                // Verificar se é a subcategoria específica "despesas de sócios"
                const subCategoryName = pathParts[1];
                if (subCategoryName && subCategoryName.toLowerCase() === 'despesas de sócios') {
                  initialData.despesasDeSociosValue += entry.amount;
                  // Não adicionar ao totalWithoutOutrasReceitas
                } else {
                  // Apenas adicionar ao total se não for a subcategoria específica
                  initialData.totalWithoutOutrasReceitas += entry.amount;
                }
              } else if (mainCategory === "(-) INVESTIMENTOS") {
                initialData.investimentos += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } 
              // ADICIONAR: Verificações para categorias de despesas pessoais
              else if (mainCategory === "🧾 Despesas Fixas" || 
                      (entry.categoryPath && entry.categoryPath.startsWith("🧾 Despesas Fixas"))) {
                initialData.despesasFixas += Math.abs(entry.amount);
                initialData.totalWithoutOutrasReceitas += entry.amount;
              } 
              else if (mainCategory === "🍞 Despesas Variáveis" || 
                      (entry.categoryPath && entry.categoryPath.startsWith("🍞 Despesas Variáveis"))) {
                initialData.despesasVariaveis += Math.abs(entry.amount);
                initialData.totalWithoutOutrasReceitas += entry.amount;
              }
              else if (mainCategory === "💳 Pagamentos e Transferências" || 
                      (entry.categoryPath && entry.categoryPath.startsWith("💳 Pagamentos e Transferências"))) {
                // Adicionar à categoria naoCategorizado para fins de cálculo
                initialData.naoCategorizado += entry.amount;
                initialData.totalWithoutOutrasReceitas += entry.amount;
              }
              else {
                 // Se não for nenhuma das categorias acima e for uma despesa/custo (valor negativo)
                 // ou uma receita não explicitamente tratada, considerar como não categorizada para o total.
                 // Isso garante que, se uma nova categoria for introduzida e não tratada explicitamente,
                 // ela ainda afete o `totalWithoutOutrasReceitas` se não for "+ OUTRAS RECEITAS...".
                 // No entanto, o ideal é que todas as categorias principais da DRE sejam explicitamente tratadas.
                 // Para o caso de "NÃO CATEGORIZADO" vindo de cashEntries, já seria pego aqui.
                 if (mainCategory !== "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
                    initialData.naoCategorizado += entry.amount; // Adiciona ao não categorizado para fins de despesas totais
                    initialData.totalWithoutOutrasReceitas += entry.amount;
                 }
              }

              console.log(`Entrada manual adicionada no Dashboard: ${formatCurrency(entry.amount)} em ${mainCategory}`);
            }
          }
        });
      } catch (error) {
        console.error("Erro ao processar entradas manuais:", error);
        // Em caso de erro, garantir que cashEntriesSnapshot seja um array vazio
        cashEntriesSnapshot = [];
      }

      // Calcular despesas totais - MODIFICADO para considerar o tipo de conta/modelo de negócio
      if (businessModel === "pessoal" || activeAccountType === 'personal') {
        // Para modelo pessoal, somar despesas fixas, variáveis e não categorizadas
        initialData.despesasTotais = Math.abs(initialData.despesasFixas) + 
                                    Math.abs(initialData.despesasVariaveis) + 
                                    Math.abs(initialData.naoCategorizado);
      } else {
        // Para modelo empresarial, manter o cálculo existente
        initialData.despesasTotais = initialData.deducoesReceita + 
                                    initialData.custosDiretos +
                                    initialData.custosServicosPrestados + // <<< ADICIONAR ESTE CAMPO
                                    initialData.despesasOperacionais + 
                                    initialData.despesasSocios - initialData.despesasDeSociosValue + 
                                    initialData.investimentos + 
                                    initialData.naoCategorizado;
      }

      // Calcular valores derivados
      initialData.lucroBruto = initialData.receitaBruta + initialData.custosDiretos + initialData.deducoesReceita; // Ajuste para incluir deduções no lucro bruto
      const resultadoOperacional = initialData.lucroBruto + initialData.despesasOperacionais;
      const resultadoAntesIR = resultadoOperacional + (initialData.despesasSocios - initialData.despesasDeSociosValue); //Ajustado

      // MODIFICADO: O resultado final agora exclui as OUTRAS RECEITAS por padrão
      initialData.resultadoFinal = initialData.totalWithoutOutrasReceitas;

      // MODIFICADO: Resultado com aportes e despesas de sócios
      initialData.resultadoFinalComAportes = initialData.resultadoFinal + initialData.outrasReceitas + initialData.despesasDeSociosValue;

      // Calcular valores específicos para modelo pessoal
      if (businessModel === "pessoal") {
        initialData.despesasFixas = Math.abs(dreStructure["🧾 Despesas Fixas"]?.total || 0);
        initialData.despesasVariaveis = Math.abs(dreStructure["🍞 Despesas Variáveis"]?.total || 0);
        initialData.saldoFinal = initialData.totalTransactions;
      }

      // Calcular custos fixos e variáveis
      const fixedCostCategories = [];
      const variableCostCategories = [];
      const uncategorizedCostCategories = [];

      let fixedCostTotal = 0;
      let variableCostTotal = 0;
      let uncategorizedCostTotal = 0;

      // Vamos contabilizar apenas as mesmas categorias que entram na despesasTotais
      // para garantir consistência nos valores totais
      const totalExpenses =
        Math.abs(initialData.deducoesReceita) +
        Math.abs(initialData.custosDiretos) +
        Math.abs(initialData.custosServicosPrestados) + // <<< ADICIONAR ESTE CAMPO
        Math.abs(initialData.despesasOperacionais) +
        Math.abs(initialData.despesasSocios - initialData.despesasDeSociosValue) + // Ajustado
        Math.abs(initialData.investimentos) + // Adicionado
        Math.abs(initialData.naoCategorizado);

      // Mapear cada transação para custos fixos ou variáveis com base nas configurações do usuário
      for (const transaction of allTransactions) {
        // Pular entradas positivas (receitas) e outras receitas
        if (transaction.amount >= 0) continue;
         const resultCheck = checkTransactionCategorization(transaction, categoryMappings);
         if (resultCheck.isCategorized && resultCheck.mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") continue;


        let categoryPath = "";
        let isCategorizedForCost = false;

        if (specificMappings[transaction.id]) {
          const specificMapping = specificMappings[transaction.id];
          categoryPath = specificMapping.categoryPath || "";
          isCategorizedForCost = true;
        }
        else {
          const result = checkTransactionCategorization(transaction, categoryMappings);
          if (result.isCategorized) {
            categoryPath = result.categoryPath;
            isCategorizedForCost = true;
          }
        }

        if (isCategorizedForCost && categoryPath) {
          const costType = costTypes[categoryPath];
          if (costType === 'fixed') {
            fixedCostTotal += Math.abs(transaction.amount);
            if (!fixedCostCategories.includes(categoryPath)) {
              fixedCostCategories.push(categoryPath);
            }
          }
          else if (costType === 'variable') {
            variableCostTotal += Math.abs(transaction.amount);
            if (!variableCostCategories.includes(categoryPath)) {
              variableCostCategories.push(categoryPath);
            }
          }
          else {
            uncategorizedCostTotal += Math.abs(transaction.amount);
            if (!uncategorizedCostCategories.includes(categoryPath)) {
              uncategorizedCostCategories.push(categoryPath);
            }
          }
        }
        else {
          uncategorizedCostTotal += Math.abs(transaction.amount);
        }
      }

      if (cashEntriesSnapshot && cashEntriesSnapshot.forEach) {
        cashEntriesSnapshot.forEach(doc => {
          const entry = doc.data();
          if (entry.categoryPath && typeof entry.amount === 'number' && entry.amount < 0) {
            //Ignorar Outras Receitas e Despesas de Sócios no cálculo de custos fixos/variáveis
             const pathParts = entry.categoryPath.split('.');
             const mainCat = pathParts[0];
             if (mainCat === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") return;
             if (mainCat === "(-) DESPESAS COM SÓCIOS" && pathParts[1] && pathParts[1].toLowerCase() === 'despesas de sócios') return;


            const costType = costTypes[entry.categoryPath];
            if (costType === 'fixed') {
              fixedCostTotal += Math.abs(entry.amount);
            }
            else if (costType === 'variable') {
              variableCostTotal += Math.abs(entry.amount);
            }
            else {
              uncategorizedCostTotal += Math.abs(entry.amount);
            }
          }
        });
      }

      const rawTotalCosts = fixedCostTotal + variableCostTotal + uncategorizedCostTotal;
      let fixedPercent = 0;
      let variablePercent = 0;
      let uncategorizedPercent = 0;

      if (rawTotalCosts > 0) {
        fixedPercent = (fixedCostTotal / rawTotalCosts) * 100;
        variablePercent = (variableCostTotal / rawTotalCosts) * 100;
        uncategorizedPercent = (uncategorizedCostTotal / rawTotalCosts) * 100;
      }
      
      const despesasTotaisRelevantes = Math.abs(initialData.despesasTotais);

      const adjustedFixedCost = despesasTotaisRelevantes > 0 ? (fixedPercent / 100) * despesasTotaisRelevantes : 0;
      const adjustedVariableCost = despesasTotaisRelevantes > 0 ? (variablePercent / 100) * despesasTotaisRelevantes : 0;
      const adjustedUncategorizedCost = despesasTotaisRelevantes > 0 ? (uncategorizedPercent / 100) * despesasTotaisRelevantes : 0;


      const costDataResult = {
        fixedCost: adjustedFixedCost,
        variableCost: adjustedVariableCost,
        uncategorizedCost: adjustedUncategorizedCost,
        totalCosts: despesasTotaisRelevantes,
        fixedPercent: fixedPercent,
        variablePercent: variablePercent,
        uncategorizedPercent: uncategorizedPercent,
        fixedCostCategories,
        variableCostCategories,
        uncategorizedCostCategories
      };

      initialData.costData = costDataResult;

      console.log("Valor da subcategoria 'despesas de sócios' excluído:", initialData.despesasDeSociosValue);
      console.log("Total de despesas de sócios:", initialData.despesasSocios);
      console.log("Despesas de sócios incluídas no cálculo:", initialData.despesasSocios - initialData.despesasDeSociosValue);

      console.log("Valores financeiros calculados (loadFinancialData):", {
        periodo: period,
        businessModel: businessModel,
        receitaBruta: initialData.receitaBruta,
        deducoesReceita: initialData.deducoesReceita,
        custosDiretos: initialData.custosDiretos,
        custosServicosPrestados: initialData.custosServicosPrestados, // <<< ADICIONAR ESTA LINHA
        despesasOperacionais: initialData.despesasOperacionais,
        despesasTotais: initialData.despesasTotais,
        lucroBruto: initialData.lucroBruto,
        outrasReceitas: initialData.outrasReceitas,
        resultadoOperacional: resultadoOperacional,
        despesasSocios: initialData.despesasSocios,
        despesasDeSociosValue: initialData.despesasDeSociosValue,
        resultadoAntesIR: resultadoAntesIR,
        investimentos: initialData.investimentos,
        naoCategorizado: initialData.naoCategorizado,
        resultadoFinal: initialData.resultadoFinal,
        resultadoFinalComAportes: initialData.resultadoFinalComAportes,
        aportesValue: initialData.aportesValue,
        totalTransactions: initialData.totalTransactions,
        totalWithoutOutrasReceitas: initialData.totalWithoutOutrasReceitas,
        despesasFixas: initialData.despesasFixas,
        despesasVariaveis: initialData.despesasVariaveis,
        saldoFinal: initialData.saldoFinal,
        costData: initialData.costData
      });

      if (initialData.receitaBruta > 0) {
        initialData.custosDiretosPercent = (Math.abs(initialData.custosDiretos) / initialData.receitaBruta) * 100;
        initialData.despesasTotaisPercent = (Math.abs(initialData.despesasTotais) / initialData.receitaBruta) * 100;
        initialData.lucroBrutoPercent = (initialData.lucroBruto / initialData.receitaBruta) * 100;
        initialData.resultadoFinalPercent = (initialData.resultadoFinal / initialData.receitaBruta) * 100;
        initialData.despesasOperacionaisPercent = (Math.abs(initialData.despesasOperacionais) / initialData.receitaBruta) * 100;
        initialData.deducoesReceitaPercent = (Math.abs(initialData.deducoesReceita) / initialData.receitaBruta) * 100;


        if (businessModel === "pessoal") {
          initialData.despesasFixasPercent = (initialData.despesasFixas / initialData.receitaBruta) * 100;
          initialData.despesasVariaveisPercent = (initialData.despesasVariaveis / initialData.receitaBruta) * 100;
          initialData.saldoFinalPercent = (initialData.saldoFinal / initialData.receitaBruta) * 100;
        }
      } else {
        initialData.custosDiretosPercent = 0;
        initialData.despesasTotaisPercent = 0;
        initialData.lucroBrutoPercent = 0;
        initialData.resultadoFinalPercent = 0;
        initialData.despesasOperacionaisPercent = 0;
        initialData.deducoesReceitaPercent = 0;


        if (businessModel === "pessoal") {
          initialData.despesasFixasPercent = 0;
          initialData.despesasVariaveisPercent = 0;
          initialData.saldoFinalPercent = 0;
        }
      }

      cacheFinancialData(period, initialData);
      setFinancialData(initialData);
      setCostData(costDataResult);

    } catch (error) {
      console.error("Erro ao carregar dados financeiros:", error);
      setError("Não foi possível carregar os dados financeiros para o período selecionado.");
    } finally {
      setLoadingFinancialData(false);
    }
  }, [cacheFinancialData, cachedFinancialData, isPeriodDataCached, businessModel, costTypes, activeAccountType, getAccountSubpath, activeBusinessProfileId]);
  
  // Helper function to process period data
  const processTransactionsForPeriod = async (period, categoryMappings) => {
    const initialData = {
      period: period.value,
      periodLabel: period.label,
      receitaBruta: 0,
      deducoesReceita: 0, // *** ADICIONADO PARA CORREÇÃO ***
      custosDiretos: 0,
      custosServicosPrestados: 0, // <<< ADICIONAR ESTA LINHA
      despesasOperacionais: 0,
      lucroBruto: 0,
      outrasReceitas: 0,
      despesasSocios: 0,
      despesasDeSociosValue: 0,
      investimentos: 0,
      naoCategorizado: 0,
      resultadoFinal: 0,
      resultadoFinalComAportes: 0,
      aportesValue: 0,
      totalTransactions: 0,
      totalWithoutOutrasReceitas: 0,
      custosTotal: 0, // Será recalculado no final
      // Campos específicos para modelo pessoal
      despesasFixas: 0,
      despesasVariaveis: 0,
      saldoFinal: 0
    };

    const dreStructure = {};
    const specificMappings = {};
    Object.entries(categoryMappings).forEach(([key, mapping]) => {
      if (mapping.isSpecificMapping && mapping.transactionId) {
        specificMappings[mapping.transactionId] = mapping;
      }
    });

    const { transactions: allTransactions } = await loadTransactionsForPeriod(period.value);

    for (const transaction of allTransactions) {
      initialData.totalTransactions += transaction.amount;
      let mainCategory = null;
      let isCategorized = false;
      // let isAporte = false; // Removido, pois aportesValue é usado diretamente
      let categoryPath = "";

      if (specificMappings[transaction.id]) {
        const specificMapping = specificMappings[transaction.id];
        mainCategory = specificMapping.groupName;
        categoryPath = specificMapping.categoryPath || "";
        isCategorized = true;
      }
      else {
        const result = checkTransactionCategorization(transaction, categoryMappings);
        if (result.isCategorized) {
          mainCategory = result.mainCategory;
          categoryPath = result.categoryPath;
          isCategorized = true;
        }
      }

      const normalizedDescription = transaction.description.trim().toLowerCase();
      if ((categoryPath && categoryPath.toLowerCase().includes('aporte')) ||
          normalizedDescription.toLowerCase().includes('aporte')) {
        // isAporte = true; // Removido
        initialData.aportesValue += transaction.amount;
      }

      if (businessModel === "pessoal" && isCategorized) {
        if (!dreStructure[mainCategory]) {
          dreStructure[mainCategory] = { total: 0 };
        }
        dreStructure[mainCategory].total += transaction.amount;
      }

      if (isCategorized) {
        // MODIFICADO: Verificação ampliada para reconhecer "💸 Receitas" de diversas formas
        if (mainCategory === "RECEITA" || 
            mainCategory === "💸 Receitas" || 
            (businessModel === "pessoal" && mainCategory === "💸 Receitas") ||
            (activeAccountType === 'personal' && mainCategory === "💸 Receitas") ||
            (activeAccountType === 'personal' && categoryPath && categoryPath.startsWith("💸 Receitas")) ||
            (categoryPath && categoryPath === "💸 Receitas.Receitas")) {
          initialData.receitaBruta += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } else if (mainCategory === "(-) DEDUÇÕES DA RECEITA") { // *** BLOCO ADICIONADO/AJUSTADO PARA CORREÇÃO ***
          initialData.deducoesReceita += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } else if (mainCategory === "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)") {
          initialData.custosDiretos += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } else if (mainCategory === "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)") { // <<< NOVA CONDIÇÃO
          initialData.custosServicosPrestados += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } else if (mainCategory === "(-) DESPESAS OPERACIONAIS") {
          initialData.despesasOperacionais += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } else if (mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
          initialData.outrasReceitas += transaction.amount;
          // Não adicionar ao totalWithoutOutrasReceitas
          continue; // Adicionado para garantir que não prossiga para totalWithoutOutrasReceitas
        } else if (mainCategory === "(-) DESPESAS COM SÓCIOS") {
          initialData.despesasSocios += transaction.amount;
          const subCategoryName = categoryPath.split('.')[1];
          if (subCategoryName && subCategoryName.toLowerCase() === 'despesas de sócios') {
            initialData.despesasDeSociosValue += transaction.amount;
          } else {
            initialData.totalWithoutOutrasReceitas += transaction.amount;
          }
        } else if (mainCategory === "(-) INVESTIMENTOS") {
          initialData.investimentos += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } 
        // ADICIONAR: Verificações para categorias de despesas pessoais
        else if (mainCategory === "🧾 Despesas Fixas" || 
                (categoryPath && categoryPath.startsWith("🧾 Despesas Fixas"))) {
          initialData.despesasFixas += Math.abs(transaction.amount);
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        } 
        else if (mainCategory === "🍞 Despesas Variáveis" || 
                (categoryPath && categoryPath.startsWith("🍞 Despesas Variáveis"))) {
          initialData.despesasVariaveis += Math.abs(transaction.amount);
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        }
        else if (mainCategory === "💳 Pagamentos e Transferências" || 
                (categoryPath && categoryPath.startsWith("💳 Pagamentos e Transferências"))) {
          // Adicionar à categoria naoCategorizado para fins de cálculo em contas pessoais
          initialData.naoCategorizado += transaction.amount;
          initialData.totalWithoutOutrasReceitas += transaction.amount;
        }
      } else {
        initialData.naoCategorizado += transaction.amount;
        initialData.totalWithoutOutrasReceitas += transaction.amount;
      }
    }

    try {
      // CORRIGIDO: Usar getAccountSubpath() em vez de lógica hardcoded
      const accountSubpath = getAccountSubpath();
      
      // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
      let userId = auth.currentUser.uid;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          userId = invitedBy; // Usar o ID do proprietário
          console.log(`Dashboard: Colaborador detectado. Processando período ${period.value} com ID do proprietário: ${userId}`);
        }
      }
      
      const cashEntriesQuery = query(
        collection(db, "cashEntries", userId, accountSubpath),
        where("period", "==", period.value)
      );
      const cashEntriesSnapshot = await getDocs(cashEntriesQuery);
      console.log(`Dashboard: Processando ${cashEntriesSnapshot.size} entradas manuais para o período ${period.value}`);
      
      cashEntriesSnapshot.forEach(docEntry => { // Renomeado 'doc' para 'docEntry' para evitar conflito com 'doc' do Firestore
        const entry = docEntry.data();
        if (entry.categoryPath && typeof entry.amount === 'number') {
          const pathParts = entry.categoryPath.split('.');
          if (pathParts.length >= 1) { // Ajustado
            const mainCategory = pathParts[0];
            const categoryName = pathParts[1] || ""; // Garantir que categoryName seja uma string
            
            initialData.totalTransactions += entry.amount;
            
            if (entry.categoryPath.toLowerCase().includes('aporte') ||
                (entry.description && entry.description.toLowerCase().includes('aporte')) ||
                categoryName.toLowerCase().includes('aporte')) {
              initialData.aportesValue += entry.amount;
            }
            
            if (businessModel === "pessoal") {
              if (!dreStructure[mainCategory]) {
                dreStructure[mainCategory] = { total: 0 };
              }
              dreStructure[mainCategory].total += entry.amount;
            }
            
            // MODIFICADO: Verificação ampliada para reconhecer "💸 Receitas" de diversas formas
            if (mainCategory === "RECEITA" || 
                mainCategory === "💸 Receitas" || 
                (businessModel === "pessoal" && mainCategory === "💸 Receitas") ||
                (activeAccountType === 'personal' && mainCategory === "💸 Receitas") ||
                (activeAccountType === 'personal' && entry.categoryPath && entry.categoryPath.startsWith("💸 Receitas")) ||
                (entry.categoryPath && entry.categoryPath === "💸 Receitas.Receitas")) {
              initialData.receitaBruta += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } else if (mainCategory === "(-) DEDUÇÕES DA RECEITA") { // *** BLOCO ADICIONADO/AJUSTADO PARA CORREÇÃO ***
              initialData.deducoesReceita += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } else if (mainCategory === "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)") {
              initialData.custosDiretos += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } else if (mainCategory === "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)") { // <<< NOVA CONDIÇÃO
              initialData.custosServicosPrestados += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } else if (mainCategory === "(-) DESPESAS OPERACIONAIS") {
              initialData.despesasOperacionais += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } else if (mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
              initialData.outrasReceitas += entry.amount;
              // Não adicionar ao totalWithoutOutrasReceitas
            } else if (mainCategory === "(-) DESPESAS COM SÓCIOS") {
              initialData.despesasSocios += entry.amount;
              const subCategoryName = categoryName;
              if (subCategoryName && subCategoryName.toLowerCase() === 'despesas de sócios') {
                initialData.despesasDeSociosValue += entry.amount;
              } else {
                initialData.totalWithoutOutrasReceitas += entry.amount;
              }
            } else if (mainCategory === "(-) INVESTIMENTOS") {
              initialData.investimentos += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } 
            // ADICIONAR: Verificações para categorias de despesas pessoais
            else if (mainCategory === "🧾 Despesas Fixas" || 
                    (entry.categoryPath && entry.categoryPath.startsWith("🧾 Despesas Fixas"))) {
              initialData.despesasFixas += Math.abs(entry.amount);
              initialData.totalWithoutOutrasReceitas += entry.amount;
            } 
            else if (mainCategory === "🍞 Despesas Variáveis" || 
                    (entry.categoryPath && entry.categoryPath.startsWith("🍞 Despesas Variáveis"))) {
              initialData.despesasVariaveis += Math.abs(entry.amount);
              initialData.totalWithoutOutrasReceitas += entry.amount;
            }
            else if (mainCategory === "💳 Pagamentos e Transferências" || 
                    (entry.categoryPath && entry.categoryPath.startsWith("💳 Pagamentos e Transferências"))) {
              // Adicionar à categoria naoCategorizado para fins de cálculo em contas pessoais
              initialData.naoCategorizado += entry.amount;
              initialData.totalWithoutOutrasReceitas += entry.amount;
            }
            else {
                 if (mainCategory !== "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
                    initialData.naoCategorizado += entry.amount;
                    initialData.totalWithoutOutrasReceitas += entry.amount;
                 }
            }
          }
        }
      });
    } catch (error) {
      console.error(`Erro ao processar entradas de dinheiro para o período ${period.value}:`, error);
    }

    initialData.resultadoFinal = initialData.totalWithoutOutrasReceitas;
    initialData.resultadoFinalComAportes = initialData.resultadoFinal + initialData.outrasReceitas + initialData.despesasDeSociosValue;

    // Calcular despesas totais - MODIFICADO para considerar o tipo de conta/modelo de negócio
    if (businessModel === "pessoal" || activeAccountType === 'personal') {
      // Para modelo pessoal, somar despesas fixas, variáveis e não categorizadas
      initialData.custosTotal = Math.abs(initialData.despesasFixas) + 
                                Math.abs(initialData.despesasVariaveis) + 
                                Math.abs(initialData.naoCategorizado);
    } else {
      // Para modelo empresarial, manter o cálculo existente
      initialData.custosTotal = Math.abs(initialData.deducoesReceita) +
                                Math.abs(initialData.custosDiretos) +
                                Math.abs(initialData.custosServicosPrestados) + // <<< ADICIONAR ESTE CAMPO
                                Math.abs(initialData.despesasOperacionais) +
                                Math.abs(initialData.despesasSocios - initialData.despesasDeSociosValue) +
                                Math.abs(initialData.investimentos) +
                                Math.abs(initialData.naoCategorizado);
    }

    initialData.lucroBruto = initialData.receitaBruta + initialData.deducoesReceita + initialData.custosDiretos; // Ajuste para incluir deduções

    if (businessModel === "pessoal") {
      initialData.despesasFixas = Math.abs(dreStructure["🧾 Despesas Fixas"]?.total || 0);
      initialData.despesasVariaveis = Math.abs(dreStructure["🍞 Despesas Variáveis"]?.total || 0);
      initialData.saldoFinal = initialData.totalTransactions;
    }

    console.log("Valores financeiros calculados (processTransactionsForPeriod):", {
        periodo: period.value,
        receitaBruta: initialData.receitaBruta,
        deducoesReceita: initialData.deducoesReceita,
        custosDiretos: initialData.custosDiretos,
        custosServicosPrestados: initialData.custosServicosPrestados, // <<< ADICIONAR ESTA LINHA
        despesasOperacionais: initialData.despesasOperacionais,
        custosTotal: initialData.custosTotal,
        lucroBruto: initialData.lucroBruto,
        outrasReceitas: initialData.outrasReceitas,
        despesasSocios: initialData.despesasSocios,
        despesasDeSociosValue: initialData.despesasDeSociosValue,
        investimentos: initialData.investimentos,
        naoCategorizado: initialData.naoCategorizado,
        resultadoFinal: initialData.resultadoFinal,
        resultadoFinalComAportes: initialData.resultadoFinalComAportes,
      });


    return initialData;
  };

  // PROGRESSIVELY LOAD PERIOD DATA: First 3 periods, then background load the rest
  const loadPeriodsSummary = useCallback(async () => {
    try {
      // Check if we already have data loaded
      const loadingStatus = getLoadingStatus();

      // If initial data is already loaded, just filter it and show it
      if (loadingStatus.initialLoaded) {
        console.log("Using cached periods data with progressive loading");
        const filteredData = getFilteredPeriods(periodsToShow);
        setPeriodsSummary(filteredData);

        // If we don't have all data loaded yet and not currently loading,
        // start background loading the remaining periods
        if (!loadingStatus.fullyLoaded && 
            !loadingBackground && 
            backgroundLoadingRef.current !== 'loading' &&
            loadingStatus.loadedCount < periods.length) {
          // Add a small delay to prevent rapid successive calls
          setTimeout(() => {
            if (!loadingBackground && backgroundLoadingRef.current !== 'loading') {
              loadRemainingPeriodsInBackground();
            }
          }, 200);
        }

        setLoadingPeriodsSummary(false);
        return;
      }

      setLoadingPeriodsSummary(true);

      if (!auth.currentUser || periods.length === 0) {
        setLoadingPeriodsSummary(false);
        return;
      }

      // Load only first three (or fewer) periods initially
      const initialPeriodsToLoad = periods.slice(0, Math.min(3, periods.length));
      const initialResults = [];

      // Carregar mapeamentos usando serviço compartilhado
      const categoryMappings = await loadCategoryMappings();

      // Process first 3 periods
      for (const period of initialPeriodsToLoad) {
        const periodData = await processTransactionsForPeriod(period, categoryMappings);
        initialResults.push(periodData);
      }

      // Cache initial data
      cacheInitialPeriods(initialResults);

      // Update UI
      setPeriodsSummary(initialResults);
      setLoadingPeriodsSummary(false);

      // If we have more periods to load, start loading them in the background
      if (periods.length > 3) {
        // Add a small delay to prevent rapid successive calls
        setTimeout(() => {
          if (!loadingBackground && backgroundLoadingRef.current !== 'loading') {
            loadRemainingPeriodsInBackground();
          }
        }, 300);
      } else {
        // Mark as fully loaded if we only had 3 or fewer periods
        markAllPeriodsLoaded();
      }
    } catch (error) {
      console.error("Erro ao carregar resumo de períodos:", error);
      setError("Não foi possível carregar o resumo de períodos.");
      setLoadingPeriodsSummary(false);
    }
  }, [
    periods,
    periodsToShow,
    cacheInitialPeriods,
    markAllPeriodsLoaded,
    getFilteredPeriods,
    getLoadingStatus,
    businessModel,
    activeAccountType,
    getAccountSubpath,
    activeBusinessProfileId,
    // Removido loadRemainingPeriodsInBackground e processTransactionsForPeriod das dependências
    // para evitar loops, já que são definidos no escopo do componente.
    // As dependências internas deles (como businessModel) já estão listadas.
  ]);


  // Load remaining periods in the background
  const loadRemainingPeriodsInBackground = useCallback(async () => {
    try {
      // Prevent multiple concurrent executions
      if (loadingBackground || backgroundLoadingRef.current === 'loading') {
        console.log("Background loading already in progress, skipping");
        return;
      }

      const loadingStatus = getLoadingStatus();
      const loadedPeriodIds = loadingStatus.loadedPeriodIds;
      const allPeriodsInState = periods;

      const remainingPeriods = allPeriodsInState.filter(p => !loadedPeriodIds.has(p.value));

      if (remainingPeriods.length === 0) {
        console.log("No remaining periods to load");
        markAllPeriodsLoaded();
        return;
      }

      // Mark as loading and set state
      backgroundLoadingRef.current = 'loading';
      setLoadingBackground(true);

      // Show loading indicator after a minimum delay (only if loading takes longer than 1 second)
      backgroundLoadingTimeoutRef.current = setTimeout(() => {
        if (backgroundLoadingRef.current === 'loading') {
          setShowBackgroundLoading(true);
        }
      }, 1000);

      console.log(`Loading ${remainingPeriods.length} additional periods in background`);

      const categoryMappings = await loadCategoryMappings();
      const additionalResults = [];

      for (const period of remainingPeriods) {
        if (backgroundLoadingRef.current === 'cancelled') {
          console.log("Background loading cancelled during processing");
          return;
        }
        const periodData = await processTransactionsForPeriod(period, categoryMappings);
        additionalResults.push(periodData);
      }
      
      if (backgroundLoadingRef.current === 'cancelled') {
        console.log("Background loading cancelled before caching");
        return;
      }

      cacheAdditionalPeriods(additionalResults);

      // Update UI with filtered periods
      const allFilteredPeriods = getFilteredPeriods(periodsToShow);
      setPeriodsSummary(allFilteredPeriods);

      console.log("Background loading of additional periods complete");
    } catch (error) {
      console.error("Erro ao carregar períodos adicionais em segundo plano:", error);
    } finally {
      // Clear timeout if it exists
      if (backgroundLoadingTimeoutRef.current) {
        clearTimeout(backgroundLoadingTimeoutRef.current);
        backgroundLoadingTimeoutRef.current = null;
      }

      // Always clean up state
      setLoadingBackground(false);
      setShowBackgroundLoading(false);
      backgroundLoadingRef.current = null;
    }
  }, [periods, cacheAdditionalPeriods, getFilteredPeriods, getLoadingStatus, markAllPeriodsLoaded, periodsToShow, activeAccountType, getAccountSubpath, activeBusinessProfileId]); // Removed loadingBackground from dependencies

  // Use o serviço compartilhado para carregar períodos
  useEffect(() => {
    const fetchInitialData = async () => {
      if (auth.currentUser) {
        setUser(auth.currentUser);

        // Carregar modelo de negócio
        try {
          const userCategoriesDoc = await getDoc(doc(db, "userCategories", auth.currentUser.uid));
          if (userCategoriesDoc.exists() && userCategoriesDoc.data().businessModel) {
            setBusinessModel(userCategoriesDoc.data().businessModel);
          }
        } catch (err) {
          console.error("Erro ao carregar modelo de negócio:", err);
        }

        // Carregar tipos de custo
        try {
          const types = await loadCostTypes(auth.currentUser.uid);
          setCostTypes(types);
        } catch (error) {
          console.error("Erro ao carregar tipos de custo:", error);
        }

        try {
          // ALTERADO: Usar a subcoleção correta
          // Buscar transações recentes para o usuário atual
          const transactionsQuery = query(
            collection(db, "transactions", auth.currentUser.uid, "documents"),
            orderBy("date", "desc"),
            limit(5)
          );

          const transactionsSnapshot = await getDocs(transactionsQuery);
          const transactionsData = [];

          transactionsSnapshot.forEach((doc) => {
            const data = doc.data();
            transactionsData.push({
              id: doc.id,
              ...data,
              date: data.date?.toDate() || new Date()
            });
          });

          setRecentTransactions(transactionsData);

          // Buscar períodos disponíveis usando o serviço refatorado
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);

          // Verificar se existe um período salvo no localStorage
          let savedPeriod = localStorage.getItem('selectedPeriod');
          let periodToUse;
          let periodLabelToUse;

          if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
            // Se existir um período salvo e ele estiver disponível, usar ele
            periodToUse = savedPeriod;
            const savedPeriodObj = availablePeriods.find(p => p.value === savedPeriod);
            periodLabelToUse = savedPeriodObj.label;
          } else if (availablePeriods.length > 0) {
            // Caso contrário, usar o período mais recente
            periodToUse = availablePeriods[0].value;
            periodLabelToUse = availablePeriods[0].label;
            // Salvar no localStorage
            localStorage.setItem('selectedPeriod', periodToUse);
          }

          if (periodToUse) {
            setCurrentPeriod(periodToUse);
            setPeriodLabel(periodLabelToUse);
            await loadFinancialData(periodToUse); // loadFinancialData já é um useCallback
          }
        } catch (error) {
          console.error("Erro ao buscar dados:", error);
          setError("Houve um erro ao carregar seus dados. Por favor, tente novamente.");
        }
      }

      setLoading(false);
    };

    fetchInitialData();
  }, [loadFinancialData, activeAccountType, getAccountSubpath, activeBusinessProfileId]); // activeAccountType, getAccountSubpath e activeBusinessProfileId como dependências

  // Verificar se a opção para mostrar aportes está ativada nas configurações
  useEffect(() => {
    const showAportesSetting = localStorage.getItem('showAportesSetting');
    setShowAportesOption(showAportesSetting === 'true');
  }, []);

  // Load periods summary when periods are available
  useEffect(() => {
    if (periods.length > 0 && auth.currentUser) { // Adicionado auth.currentUser para garantir que o usuário está carregado
      loadPeriodsSummary();
    }
  }, [periods, loadPeriodsSummary, auth.currentUser, activeAccountType, getAccountSubpath, activeBusinessProfileId]); // Adicionado activeAccountType, getAccountSubpath e activeBusinessProfileId

  // Cleanup background loading task on component unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing background loading task
      console.log("Dashboard unmounting, cancelling background load if any.");
      backgroundLoadingRef.current = 'cancelled';
      
      // Clear timeout if it exists
      if (backgroundLoadingTimeoutRef.current) {
        clearTimeout(backgroundLoadingTimeoutRef.current);
        backgroundLoadingTimeoutRef.current = null;
      }
    };
  }, []);

  // Função para tratar mudança de período
  const handlePeriodChange = async (event) => {
    const period = event.target.value;
    setCurrentPeriod(period);

    // Remove focus from the dropdown immediately after selection
    event.target.blur();

    // Encontrar o label do período selecionado
    const selectedPeriod = periods.find(p => p.value === period);
    if (selectedPeriod) {
      setPeriodLabel(selectedPeriod.label);
    }

    // Salvar o período selecionado no localStorage
    localStorage.setItem('selectedPeriod', period);

    await loadFinancialData(period);
  };

  // Função para tratar mudança na quantidade de períodos a mostrar
  const handlePeriodsToShowChange = (event) => {
    const value = parseInt(event.target.value);
    setPeriodsToShow(value);

    localStorage.setItem('periodsToShow', value.toString());
    event.target.blur();

    const loadingStatus = getLoadingStatus();
    if (loadingStatus.initialLoaded) {
      const filteredData = getFilteredPeriods(value);
      setPeriodsSummary(filteredData);

      const totalAvailablePeriods = periods.length;
      // Only start background loading if:
      // 1. We need more data than what's currently loaded
      // 2. We're not fully loaded yet
      // 3. There are actually more periods to load
      // 4. We're not already loading in background
      const needsMoreData = (value === 0 || value > loadingStatus.loadedCount);
      const hasMoreToLoad = loadingStatus.loadedCount < totalAvailablePeriods;
      const notCurrentlyLoading = !loadingBackground && backgroundLoadingRef.current !== 'loading';
      
      if (needsMoreData && !loadingStatus.fullyLoaded && hasMoreToLoad && notCurrentlyLoading) {
        // Add a small delay to prevent rapid successive calls
        setTimeout(() => {
          if (!loadingBackground && backgroundLoadingRef.current !== 'loading') {
            loadRemainingPeriodsInBackground();
          }
        }, 100);
      }
    }
  };


  // Handle includeExcluidos toggle change - ALTERADO O NOME
  const handleIncludeExcluidosChange = () => {
    const newValue = !includeExcluidos;
    setIncludeExcluidos(newValue);

    // Save preference to localStorage
    localStorage.setItem('includeExcluidos', newValue.toString());
  };

  // Formatar valor monetário
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Formatar data
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Seletor de período */}
      <div className="period-selector-container">
        <div className="period-selector-header">
          <h2>Dados Financeiros {periodLabel && `- ${periodLabel}`}</h2>
          <div className="period-dropdown">
            <label htmlFor="period-select">Período: </label>
            <select
              id="period-select"
              value={currentPeriod}
              onChange={handlePeriodChange}
              disabled={loadingFinancialData || periods.length === 0}
            >
              {periods.length === 0 ? (
                <option value="">Nenhum período disponível</option>
              ) : (
                periods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Cards Financeiros - Modificado para modelo pessoal */}
      <div className="card-container" style={{marginBottom: "20px"}}>
        {/* Card 1 - RECEITAS (permanece igual para todos os modelos) */}
        <div className="fin-card fin-card-receipts">
          <div className="fin-card-title">RECEITAS</div>
          <div className="fin-card-value">
            {loadingFinancialData ? (
              <span className="loading-text">Carregando...</span>
            ) : (
              formatCurrency(financialData.receitaBruta)
            )}
          </div>
          <div className="fin-card-description">Total de receitas no período</div>
          <div className="fin-card-footer">
            <div className="empty-space"></div>
          </div>
        </div>


        {/* Card 2 - Varia conforme o modelo */}
        <div className="fin-card" style={{borderLeftColor: '#36b9cc'}}>
          <div className="fin-card-title">
            {businessModel === "pessoal" ? "DESPESAS FIXAS" : "DESPESAS TOTAIS"}
          </div>
          <div className="fin-card-value">
            {loadingFinancialData ? (
              <span className="loading-text">Carregando...</span>
            ) : (
              formatCurrency(
                businessModel === "pessoal"
                  ? financialData.despesasFixas
                  : Math.abs(financialData.despesasTotais)
              )
            )}
          </div>
          <div className="fin-card-description">
            {businessModel === "pessoal"
              ? "Total de despesas fixas do mês"
              : "Deduções + CMV + Despesas Operacionais"}
          </div>
          <div className="fin-card-footer">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${businessModel === "pessoal" ? financialData.despesasFixasPercent : financialData.despesasTotaisPercent}%`,
                  backgroundColor: '#36b9cc'
                }}
              ></div>
            </div>
            <div className="progress-text">
              {loadingFinancialData ?
                "Calculando..." :
                (businessModel === "pessoal"
                  ? `${Math.round(financialData.despesasFixasPercent || 0)}% das receitas`
                  : `${Math.round(financialData.despesasTotaisPercent || 0)}% das receitas`)
              }
            </div>
          </div>
        </div>

        {/* Card 3 - Substituído por Custos Fixos/Variáveis */}
        <div className="fin-card fin-card-profit">
          <div className="fin-card-title">
            CUSTOS FIXOS/VARIÁVEIS
          </div>
          <div className="fin-card-value">
            {loadingFinancialData ? (
              <span className="loading-text">Carregando...</span>
            ) : (
              <>
                <span className="fixed-cost-value">{formatCurrency(costData.fixedCost || 0)}</span>
                <span className="cost-separator"> / </span>
                <span className="variable-cost-value">{formatCurrency(costData.variableCost || 0)}</span>
              </>
            )}
          </div>
          <div className="fin-card-description">
             Fixos: {Math.round(costData.fixedPercent || 0)}% • Variáveis: {Math.round(costData.variablePercent || 0)}%
             {costData.uncategorizedCost > 0 && ` • NC: ${Math.round(costData.uncategorizedPercent || 0)}%`}
          </div>
          <div className="fin-card-footer">
            <div className="progress-bar costs-bar">
              {/* Barra para custos fixos */}
              <div
                className="progress-fill fixed-costs"
                title={`Fixos: ${formatCurrency(costData.fixedCost || 0)} (${Math.round(costData.fixedPercent || 0)}%)`}
                style={{
                  width: `${costData.fixedPercent || 0}%`,
                  backgroundColor: '#3b82f6'
                }}
              ></div>
              {/* Barra para custos variáveis */}
              <div
                className="progress-fill variable-costs"
                title={`Variáveis: ${formatCurrency(costData.variableCost || 0)} (${Math.round(costData.variablePercent || 0)}%)`}
                style={{
                  width: `${costData.variablePercent || 0}%`,
                  left: `${costData.fixedPercent || 0}%`,
                  backgroundColor: '#10b981'
                }}
              ></div>
              {/* Barra para custos não categorizados (em custos) - só aparece se houver */}
              {costData.uncategorizedCost > 0 && (
                <div
                  className="progress-fill uncategorized-costs"
                  title={`Não Cat. (Custos): ${formatCurrency(costData.uncategorizedCost || 0)} (${Math.round(costData.uncategorizedPercent || 0)}%)`}
                  style={{
                    width: `${costData.uncategorizedPercent || 0}%`,
                    left: `${(costData.fixedPercent || 0) + (costData.variablePercent || 0)}%`,
                    backgroundColor: '#9ca3af'
                  }}
                ></div>
              )}
            </div>
             <div className="progress-text">
                {loadingFinancialData ?
                    "Calculando..." :
                    `${Math.round( ( (costData.fixedCost + costData.variableCost + costData.uncategorizedCost) / Math.abs(financialData.despesasTotais) ) * 100 || 0)}% do total de despesas`
                }
            </div>
          </div>
        </div>

        {/* Card 4 - Varia conforme o modelo */}
        <div className="fin-card fin-card-result">
          <div className="fin-card-title">
            {businessModel === "pessoal" ? "SALDO FINAL" : "RESULTADO FINAL"}
          </div>
          <div className="fin-card-value">
            {loadingFinancialData ? (
              <span className="loading-text">Carregando...</span>
            ) : (
              formatCurrency(
                businessModel === "pessoal"
                  ? financialData.saldoFinal
                  : (includeExcluidos ? financialData.resultadoFinalComAportes : financialData.resultadoFinal)
              )
            )}
          </div>
          <div className="fin-card-description">
            {businessModel === "pessoal"
              ? "Saldo final do período"
              : `Resultado líquido do período ${includeExcluidos ? "(com aportes e outras receitas/despesas de sócios)" : ""}`}
          </div>

          <div className="fin-card-footer">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${businessModel === "pessoal" ? financialData.saldoFinalPercent : financialData.resultadoFinalPercent}%`,
                  backgroundColor: (businessModel === "pessoal" ? financialData.saldoFinal : (includeExcluidos ? financialData.resultadoFinalComAportes : financialData.resultadoFinal)) >=0 ? '#4caf50' : '#ff9800'
                }}
              ></div>
            </div>
            <div className="progress-text">
              {loadingFinancialData ?
                "Calculando..." :
                (businessModel === "pessoal"
                  ? `${Math.round(financialData.saldoFinalPercent || 0)}% das receitas`
                  : `${Math.round(financialData.resultadoFinalPercent || 0)}% das receitas`)
              }
            </div>
          </div>
        </div>
      </div>

      {/* Period Summary Section */}
      <div className="period-summary-section">
        <div className="section-header">
          <h2>Evolução dos Resultados</h2>
          <div className="summary-controls">
            {/* Seletor para quantidade de períodos */}
            <div className="periods-to-show-dropdown">
              <label htmlFor="periods-to-show-select">Mostrar: </label>
              <select
                id="periods-to-show-select"
                value={periodsToShow}
                onChange={handlePeriodsToShowChange}
                disabled={loadingPeriodsSummary}
                className="period-select"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Botão condicionalmente visível com base na configuração do usuário */}
            {showAportesOption && businessModel !== "pessoal" && (
              <div className="exclude-itens-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={includeExcluidos}
                    onChange={handleIncludeExcluidosChange}
                  />
                  <span className="checkbox-text">
                    Mostrar aportes, outras receitas e despesas de sócios ao resultado final
                  </span>
                </label>
                {includeExcluidos && <span className="filter-active">Filtro ativo</span>}
              </div>
            )}
          </div>
        </div>

        {loadingPeriodsSummary ? (
          <div className="summary-loading">
            <div className="loading-spinner"></div>
            <p>Carregando resumo de períodos...</p>
          </div>
        ) : periodsSummary.length === 0 ? (
          <div className="no-summary-data">
            <p>Não há dados suficientes para mostrar a evolução dos resultados.</p>
          </div>
        ) : (
          <>
            {/* Indicator for background loading */}
            {showBackgroundLoading && (
              <div className="background-loading-indicator">
                <div className="background-loading-spinner"></div>
                <p>Carregando dados adicionais em segundo plano...</p>
              </div>
            )}

            {/* Summary Cards View */}
            <div className="summary-cards-container">
              {periodsSummary.map((period, index) => {
                // Determine if the result is positive based on businessModel and includeExcluidos setting
                const finalResult = businessModel === "pessoal" ?
                  period.saldoFinal :
                  (includeExcluidos ? period.resultadoFinalComAportes : period.resultadoFinal);

                const isPositiveResult = finalResult >= 0;

                return (
                  <div
                    key={index}
                    className={`summary-period-card ${isPositiveResult ? 'result-positive' : 'result-negative'}`}
                  >
                    <div className="period-card-header">
                      <span className="period-name">{period.periodLabel}</span>
                      <span className={`period-result ${isPositiveResult ? 'positive' : 'negative'}`}>
                        {formatCurrency(finalResult)}
                      </span>
                    </div>

                    <div className="period-card-details">
                      {businessModel === "pessoal" ? (
                        <>
                          <div className="period-metric">
                            <span className="metric-label">Receitas</span>
                            <span className="metric-value positive">{formatCurrency(period.receitaBruta)}</span>
                          </div>
                          <div className="period-metric">
                            <span className="metric-label">Despesas Fixas</span>
                            <span className="metric-value negative">{formatCurrency(Math.abs(period.despesasFixas))}</span>
                          </div>
                          <div className="period-metric">
                            <span className="metric-label">Despesas Variáveis</span>
                            <span className="metric-value negative">{formatCurrency(Math.abs(period.despesasVariaveis))}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="period-metric">
                            <span className="metric-label">Receita Líquida</span>
                            <span className="metric-value positive">{formatCurrency(period.receitaBruta + period.deducoesReceita)}</span>
                          </div>
                          <div className="period-metric">
                            <span className="metric-label">Custos/Despesas Totais</span>
                            <span className="metric-value negative">{formatCurrency(period.custosTotal)}</span>
                          </div>
                          <div className="period-metric">
                            <span className="metric-label">Lucro Bruto</span>
                            <span className={`metric-value ${period.lucroBruto >= 0 ? 'positive' : 'negative'}`}>
                              {formatCurrency(period.lucroBruto)}
                            </span>
                          </div>

                          {includeExcluidos && (
                            <>
                              {period.outrasReceitas !== 0 && ( // Mostrar se for diferente de zero (pode ser negativo)
                                <div className="period-metric aportes">
                                  <span className="metric-label">Outras Receitas</span>
                                  <span className={`metric-value ${period.outrasReceitas >= 0 ? 'positive' : 'negative'}`}>{formatCurrency(period.outrasReceitas)}</span>
                                </div>
                              )}
                              {period.despesasDeSociosValue !== 0 && ( // Mostrar se for diferente de zero
                                <div className="period-metric despesas-socios">
                                  <span className="metric-label">Despesas de Sócios (Subcat.)</span>
                                  <span className="metric-value negative">{formatCurrency(Math.abs(period.despesasDeSociosValue))}</span>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <div className="period-card-chart">
                      <div className="micro-chart">
                        <div className="chart-bar">
                        <div
                            className="chart-fill"
                            style={{
                              width: `${Math.min(100, Math.max(0, businessModel === "pessoal" ?
                                ((period.receitaBruta > 0 ? (period.saldoFinal / period.receitaBruta) * 100 : (period.saldoFinal > 0 ? 100 : 0))) :
                                (( (period.receitaBruta + period.deducoesReceita) > 0 ? (finalResult / (period.receitaBruta + period.deducoesReceita) ) * 100 : (finalResult > 0 ? 100 : 0) ))
                              || 0))}%`,
                              backgroundColor: isPositiveResult ? '#4caf50' : '#f44336'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Table */}
            <div className="summary-table-container">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Período</th>
                    {businessModel === "pessoal" ? (
                      <>
                        <th>Receitas</th>
                        <th>Despesas Fixas</th>
                        <th>Despesas Variáveis</th>
                        <th>Saldo Final</th>
                      </>
                    ) : (
                      <>
                        <th>Receita Bruta</th>
                        {/* Conditionally render "Deduções da Receita" header */}
                        {shouldShowRevenueDeductions && (
                          <th>Deduções da Receita</th>
                        )}
                        <th>Custos/Desp. Totais</th>
                        <th>Lucro Bruto</th>
                        {includeExcluidos && (
                          <>
                            <th>Outras Receitas</th>
                            <th>Despesas de Sócios (Subcat.)</th>
                          </>
                        )}
                        <th>Resultado Final</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {periodsSummary.map((period, index) => (
                    <tr key={index}>
                      <td>{period.periodLabel}</td>
                      {businessModel === "pessoal" ? (
                        <>
                          <td className="amount-positive">{formatCurrency(period.receitaBruta)}</td>
                          <td className="amount-negative">{formatCurrency(Math.abs(period.despesasFixas))}</td>
                          <td className="amount-negative">{formatCurrency(Math.abs(period.despesasVariaveis))}</td>
                          <td className={period.saldoFinal >= 0 ? "amount-positive" : "amount-negative"}>
                            {formatCurrency(period.saldoFinal)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="amount-positive">{formatCurrency(period.receitaBruta)}</td>
                          {/* Conditionally render "Deduções da Receita" data cell */}
                          {shouldShowRevenueDeductions && (
                            <td className="amount-negative">{formatCurrency(Math.abs(period.deducoesReceita))}</td>
                          )}
                          <td className="amount-negative">{formatCurrency(period.custosTotal)}</td>
                          <td className={period.lucroBruto >= 0 ? "amount-positive" : "amount-negative"}>
                            {formatCurrency(period.lucroBruto)}
                          </td>
                          {includeExcluidos && (
                            <>
                              <td className={period.outrasReceitas >= 0 ? "amount-positive" : "amount-negative"}>{formatCurrency(period.outrasReceitas)}</td>
                              <td className="amount-negative">{formatCurrency(Math.abs(period.despesasDeSociosValue || 0))}</td>
                            </>
                          )}
                          <td className={
                            includeExcluidos ?
                              (period.resultadoFinalComAportes >= 0 ? "amount-positive" : "amount-negative") :
                              (period.resultadoFinal >= 0 ? "amount-positive" : "amount-negative")
                          }>
                            {formatCurrency(includeExcluidos ? period.resultadoFinalComAportes : period.resultadoFinal)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}

                  {/* Totals row */}
                  <tr className="total-row">
                    <td><strong>TOTAL</strong></td>
                    {businessModel === "pessoal" ? (
                      <>
                        <td className="amount-positive">
                          {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.receitaBruta, 0))}
                        </td>
                        <td className="amount-negative">
                          {formatCurrency(Math.abs(periodsSummary.reduce((sum, period) => sum + period.despesasFixas, 0)))}
                        </td>
                        <td className="amount-negative">
                          {formatCurrency(Math.abs(periodsSummary.reduce((sum, period) => sum + period.despesasVariaveis, 0)))}
                        </td>
                        <td className={periodsSummary.reduce((sum, period) => sum + period.saldoFinal, 0) >= 0 ? "amount-positive" : "amount-negative"}>
                          {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.saldoFinal, 0))}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="amount-positive">
                          {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.receitaBruta, 0))}
                        </td>
                        {/* Conditionally render "Deduções da Receita" total cell */}
                        {shouldShowRevenueDeductions && (
                          <td className="amount-negative">
                            {formatCurrency(Math.abs(periodsSummary.reduce((sum, period) => sum + period.deducoesReceita, 0)))}
                          </td>
                        )}
                        <td className="amount-negative">
                          {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.custosTotal, 0))}
                        </td>
                        <td className={periodsSummary.reduce((sum, period) => sum + period.lucroBruto, 0) >= 0 ? "amount-positive" : "amount-negative"}>
                          {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.lucroBruto, 0))}
                        </td>
                        {includeExcluidos && (
                          <>
                            <td className={periodsSummary.reduce((sum, period) => sum + period.outrasReceitas, 0) >= 0 ? "amount-positive" : "amount-negative"}>
                              {formatCurrency(periodsSummary.reduce((sum, period) => sum + period.outrasReceitas, 0))}
                            </td>
                            <td className="amount-negative">
                              {formatCurrency(Math.abs(periodsSummary.reduce((sum, period) => sum + (period.despesasDeSociosValue || 0), 0)))}
                            </td>
                          </>
                        )}
                        <td className={
                          includeExcluidos ?
                            (periodsSummary.reduce((sum, period) => sum + period.resultadoFinalComAportes, 0) >= 0 ? "amount-positive" : "amount-negative") :
                            (periodsSummary.reduce((sum, period) => sum + period.resultadoFinal, 0) >= 0 ? "amount-positive" : "amount-negative")
                        }>
                          {formatCurrency(
                            includeExcluidos ?
                              periodsSummary.reduce((sum, period) => sum + period.resultadoFinalComAportes, 0) :
                              periodsSummary.reduce((sum, period) => sum + period.resultadoFinal, 0)
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Transações recentes */}
      {recentTransactions.length > 0 && (
        <div className="transactions-section">
          <div className="section-header">
            <h2 className="section-title">Transações Recentes</h2>
            <button
              className="view-all-button"
              onClick={() => window.location.href = '/transactions'}
            >
              Ver Todas as Transações
            </button>
          </div>

          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.date)}</td>
                    <td>{transaction.description}</td>
                    <td>
                      <span className="category-badge">{transaction.categoryPathDb || transaction.category || "Não categorizado"}</span>
                    </td>
                    <td className={transaction.amount >= 0 ? 'amount-positive' : 'amount-negative'}>
                      {formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Dashboard;